import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Title, Tabs, Metric, Table, Spinner, Button, Modal, useToast, DateInput, NumberInput, Selectbox } from './bpm';
import { portfolioAPI, twsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCanEdit } from '../hooks/useCanEdit';
import { getPageIcon } from '../utils/pageIcons';
import { formatCurrency, formatEuropeanCurrency, getCurrencySymbol } from '../utils/formatCurrency';
import { calculateXIRR } from '../utils/xirr';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { usePageRenderTime, logDataLoad } from '../utils/performanceLogger';
import './Compte.css';

const Compte = ({ onLogoClick = null }) => {
  usePageRenderTime('Compte');
  const [activeTab, setActiveTab] = useState(0);
  const [chartKey, setChartKey] = useState(0);
  const { showToast } = useToast();
  const { profile } = useAuth();
  const { canEdit } = useCanEdit();
  const pageIcon = getPageIcon('Compte');
  
  // États généraux
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('EUR');
  
  const chartContainerRef = useRef(null);
  const [isChartVisible, setIsChartVisible] = useState(false);
  const [chartContainerDims, setChartContainerDims] = useState({ width: 0, height: 0 });

  // Références pour les carrousels de métriques
  const carouselRefRisk = useRef(null);
  const carouselRefPerf = useRef(null);
  const carouselRefGainsLosses = useRef(null);
  const carouselRefInvestisseurs = useRef(null);
  const carouselRefTri = useRef(null);

  const scrollCarousel = (ref, direction) => {
    if (ref?.current) {
      const scrollAmount = 300;
      ref.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };
  
  // Forcer le re-render du graphique quand l'onglet Performance devient actif
  useEffect(() => {
    if (activeTab === 0) {
      // Délai pour permettre au conteneur de se rendre correctement
      const timer = setTimeout(() => {
        setIsChartVisible(true);
        setChartKey(prev => prev + 1);
      }, 150);
      return () => {
        clearTimeout(timer);
        setIsChartVisible(false);
      };
    } else {
      setIsChartVisible(false);
    }
  }, [activeTab]);
  
  // Observer les changements de taille du conteneur avec debounce
  const lastChartDimsRef = useRef({ width: 0, height: 0 });
  const resizeTimeoutRef = useRef(null);
  /** Une seule requête /portfolio/valuations/current en vol (évite 3 appels parallèles → 15s chacun). */
  const currentValuationRequestRef = useRef(null);
  
  useEffect(() => {
    if (!chartContainerRef.current || !isChartVisible) {
      if (!isChartVisible) setChartContainerDims({ width: 0, height: 0 });
      return;
    }
    
    const checkDims = () => {
      const rect = chartContainerRef.current?.getBoundingClientRect();
      if (rect && rect.width > 0 && rect.height > 0) {
        setChartContainerDims({ width: rect.width, height: rect.height });
      }
    };
    // Mesurer après le layout (évite width/height -1 du ResponsiveContainer Recharts)
    let rafId = requestAnimationFrame(() => {
      checkDims();
    });
    
    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        const rect = chartContainerRef.current?.getBoundingClientRect();
        if (rect && rect.width > 0 && rect.height > 0) {
          const newDims = { width: rect.width, height: rect.height };
          setChartContainerDims(newDims);
          const widthDiff = Math.abs(newDims.width - lastChartDimsRef.current.width);
          const heightDiff = Math.abs(newDims.height - lastChartDimsRef.current.height);
          if (widthDiff > 5 || heightDiff > 5) {
            lastChartDimsRef.current = newDims;
            setChartKey(prev => prev + 1);
          }
        }
      }, 500);
    });
    
    resizeObserver.observe(chartContainerRef.current);
    
    return () => {
      cancelAnimationFrame(rafId);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeObserver.disconnect();
    };
  }, [isChartVisible]);
  
  // États pour Performance (Onglet 1)
  const [performanceData, setPerformanceData] = useState(null);
  const [valuationEvolution, setValuationEvolution] = useState(null);
  const [plData, setPlData] = useState(null);
  const [maxGainsLosses, setMaxGainsLosses] = useState(null);
  const [performanceDateRange, setPerformanceDateRange] = useState({
    start: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  
  // États pour TRI (Onglet 2)
  const [triData, setTriData] = useState(null);
  const [triCashflows, setTriCashflows] = useState([]);
  const [latestValuation, setLatestValuation] = useState(null);
  const [currentValuation, setCurrentValuation] = useState(null);
  
  // États pour Apports/Retraits (Onglet 3)
  const [apports, setApports] = useState([]);
  const [loadingApports, setLoadingApports] = useState(false);
  const [selectedApports, setSelectedApports] = useState([]);
  const [showAddApportModal, setShowAddApportModal] = useState(false);
  const [showEditApportModal, setShowEditApportModal] = useState(false);
  const [editingApport, setEditingApport] = useState(null);
  
  // États pour Investisseurs (Onglet 4)
  const [investisseurs, setInvestisseurs] = useState([]);
  const [loadingInvestisseurs, setLoadingInvestisseurs] = useState(false);
  const [selectedInvestisseurs, setSelectedInvestisseurs] = useState([]);
  const [showAddInvestisseurModal, setShowAddInvestisseurModal] = useState(false);
  const [showEditInvestisseurModal, setShowEditInvestisseurModal] = useState(false);
  const [editingInvestisseur, setEditingInvestisseur] = useState(null);
  const [investisseursValues, setInvestisseursValues] = useState([]);
  
  // États pour Valorisations (Onglet 5)
  const [valorisations, setValorisations] = useState([]);
  const [loadingValorisations, setLoadingValorisations] = useState(false);
  const [loadingImportCsv, setLoadingImportCsv] = useState(false);
  const valorisationCsvInputRef = useRef(null);
  const [selectedValorisations, setSelectedValorisations] = useState([]);
  const [showAddValorisationModal, setShowAddValorisationModal] = useState(false);
  const [showEditValorisationModal, setShowEditValorisationModal] = useState(false);
  const [editingValorisation, setEditingValorisation] = useState(null);
  
  // États pour Commentaires (Onglet 6)
  const [commentaires, setCommentaires] = useState([]);
  const [loadingCommentaires, setLoadingCommentaires] = useState(false);
  const [selectedCommentaires, setSelectedCommentaires] = useState([]);
  const [showAddCommentaireModal, setShowAddCommentaireModal] = useState(false);
  const [showEditCommentaireModal, setShowEditCommentaireModal] = useState(false);
  const [editingCommentaire, setEditingCommentaire] = useState(null);
  
  // Form states pour les modals
  const [apportForm, setApportForm] = useState({
    date_apport: new Date().toISOString().split('T')[0],
    type_flux: 'apport',
    montant: '',
    description: '',
    investisseur: ''
  });
  
  const [investisseurForm, setInvestisseurForm] = useState({
    nom: '',
    prenom: '',
    type_investisseur: 'personne_physique',
    email: '',
    telephone: '',
    adresse: '',
    commentaires: ''
  });
  
  const [valorisationForm, setValorisationForm] = useState({
    date_valorisation: new Date().toISOString().split('T')[0],
    valeur_nette: '',
    currency: 'EUR'
  });
  
  const [commentaireForm, setCommentaireForm] = useState({
    date_commentaire: new Date().toISOString().split('T')[0],
    commentaire: ''
  });
  
  // Charger les données au montage et quand on change d'onglet
  useEffect(() => {
    if (activeTab === 0) {
      // Onglet Performance (graphique, métriques, evolution par investisseur)
      loadPerformanceData();
      loadTRIData();
      loadInvestisseursValues();
    } else if (activeTab === 1) {
      // Onglet TRI
      loadTRIData();
    } else if (activeTab === 2) {
      // Onglet P&L (mêmes données que Performance)
      loadPerformanceData();
    } else if (activeTab === 3) {
      // Onglet Apports/Retraits - charger aussi les investisseurs pour le formulaire
      loadApports();
      loadInvestisseurs();
    } else if (activeTab === 4) {
      // Onglet Investisseurs
      loadInvestisseurs();
      loadInvestisseursValues();
    } else if (activeTab === 5) {
      // Onglet Valorisations
      loadValorisations();
    } else if (activeTab === 6) {
      // Onglet Commentaires
      loadCommentaires();
    }
  }, [activeTab]);
  
  // Charger la valorisation actuelle depuis IBKR (une seule requête partagée avec loadTRIData)
  useEffect(() => {
    loadCurrentValuation();
  }, []);
  
  // Fonctions de chargement des données
  const loadCurrentValuation = useCallback(async () => {
    const t0 = performance.now();
    try {
      let promise = currentValuationRequestRef.current;
      if (!promise) {
        promise = portfolioAPI.getCurrentValuation();
        currentValuationRequestRef.current = promise;
      }
      const response = await promise;
      if (response?.data) {
        setCurrentValuation(response.data);
        setCurrency(response.data.currency || 'EUR');
      }
    } catch (error) {
      console.error('Erreur chargement valorisation actuelle:', error);
    } finally {
      currentValuationRequestRef.current = null;
      const duration = performance.now() - t0;
      logDataLoad('Compte - Valorisation actuelle', duration, 'IBKR');
    }
  }, []);
  
  const loadPerformanceData = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        portfolioAPI.getValuationEvolution(
          performanceDateRange.start,
          performanceDateRange.end,
          1,
          1,
          true
        ),
        portfolioAPI.getPLData(
          performanceDateRange.start,
          performanceDateRange.end
        ),
        portfolioAPI.getMaxGainsLosses(
          performanceDateRange.start,
          performanceDateRange.end
        )
      ]);
      
      const [evolutionRes, plRes, maxRes] = results.map((r) => 
        r.status === 'fulfilled' ? r.value : null
      );
      
      if (evolutionRes?.data) setValuationEvolution(evolutionRes.data);
      else setValuationEvolution(null);
      if (plRes?.data) setPlData(plRes.data);
      else setPlData(null);
      if (maxRes?.data) setMaxGainsLosses(maxRes.data);
      else setMaxGainsLosses(null);
      
      // Toast uniquement si toutes les requêtes ont échoué (hors 404)
      const hasData = evolutionRes?.data || plRes?.data || maxRes?.data;
      const hasRealError = results.some(
        (r) => r.status === 'rejected' && r.reason?.response?.status !== 404
      );
      if (!hasData && hasRealError) {
        showToast('Erreur lors du chargement des données de performance', 'error', 5000, 'Erreur de chargement', 'Performance', pageIcon);
      }
    } catch (error) {
      console.error('Erreur chargement données performance:', error);
      // Ne pas afficher de toast pour 404 (pas de données = cas normal)
      const status = error?.response?.status;
      if (status !== 404) {
        showToast('Erreur lors du chargement des données de performance', 'error', 5000, 'Erreur de chargement', 'Performance', pageIcon);
      }
    } finally {
      const duration = performance.now() - t0;
      setLoading(false);
      logDataLoad('Compte - Performance', duration, 'évolution + P&L + gains/pertes');
    }
  }, [performanceDateRange, showToast, pageIcon]);
  
  const loadTRIData = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      // Réutiliser currentValuation en state ou la requête déjà en vol pour éviter 2–3 appels à /valuations/current
      const currentPromise = currentValuation
        ? Promise.resolve({ data: currentValuation })
        : (currentValuationRequestRef.current || (currentValuationRequestRef.current = portfolioAPI.getCurrentValuation()));
      const results = await Promise.allSettled([
        portfolioAPI.getApports(),
        portfolioAPI.getLatestValuation(),
        currentPromise
      ]);
      if (!currentValuation) currentValuationRequestRef.current = null;

      const apportsRes = results[0].status === 'fulfilled' ? results[0].value : null;
      const latestRes = results[1].status === 'fulfilled' ? results[1].value : null;
      const currentRes = results[2].status === 'fulfilled' ? results[2].value : null;
      
      if (apportsRes?.data) {
        setTriCashflows(apportsRes.data);
        setApports(apportsRes.data); // pour le graphique « Évolution » (courbe Apports YTD) sur l’onglet Performance
        // Calculer le TRI
        const cashflows = apportsRes.data
          .filter(a => a.actif)
          .map(a => [a.date_apport, parseFloat(a.montant)]);
        
        // Utiliser la valorisation actuelle (IBKR) ou la dernière enregistrée (404 = pas de données)
        const valuation = currentRes?.data || latestRes?.data;
        let valuationValue = 0;
        if (valuation) {
          valuationValue = valuation.net_liquidation ?? valuation.valeur_totale ?? 0;
          const valuationDate = valuation.date || valuation.date_valorisation;
          cashflows.push([valuationDate, parseFloat(valuationValue)]);
        }
        
        if (cashflows.length >= 2) {
          const tri = calculateXIRR(cashflows);
          setTriData({
            tri: tri ? tri * 100 : null,
            totalApports: apportsRes.data
              .filter(a => a.actif && parseFloat(a.montant) < 0)
              .reduce((sum, a) => sum + Math.abs(parseFloat(a.montant)), 0),
            totalRetraits: apportsRes.data
              .filter(a => a.actif && parseFloat(a.montant) > 0)
              .reduce((sum, a) => sum + parseFloat(a.montant), 0),
            netInvesti: 0,
            valorisation: parseFloat(valuationValue),
            plusValue: 0
          });
        }
      } else {
        setTriCashflows([]);
        setApports([]);
      }
      
      if (latestRes?.data) setLatestValuation(latestRes.data);
      else setLatestValuation(null);
      if (currentRes?.data) setCurrentValuation(currentRes.data);
      else setCurrentValuation(null);
      
      // Toast uniquement si getApports a échoué (hors 404)
      const apportsFailed = results[0].status === 'rejected' && results[0].reason?.response?.status !== 404;
      if (apportsFailed) {
        showToast('Erreur lors du chargement des données TRI', 'error', 5000, null, 'Compte', pageIcon);
      }
    } catch (error) {
      console.error('Erreur chargement données TRI:', error);
      // Ne pas afficher de toast pour 404 (pas de données = cas normal)
      const status = error?.response?.status;
      if (status !== 404) {
        showToast('Erreur lors du chargement des données TRI', 'error', 5000, null, 'Compte', pageIcon);
      }
    } finally {
      const duration = performance.now() - t0;
      setLoading(false);
      logDataLoad('Compte - TRI', duration, 'apports + valorisations + XIRR');
    }
  }, [currentValuation, showToast, pageIcon]);
  
  const loadApports = useCallback(async () => {
    const t0 = performance.now();
    setLoadingApports(true);
    try {
      const response = await portfolioAPI.getApports();
      if (response.data) {
        setApports(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement apports:', error);
      showToast('Erreur lors du chargement des apports', 'error', 5000, null, 'Compte', pageIcon);
    } finally {
      setLoadingApports(false);
      const duration = performance.now() - t0;
      logDataLoad('Compte - Apports', duration);
    }
  }, [showToast, pageIcon]);
  
  const loadInvestisseurs = useCallback(async () => {
    const t0 = performance.now();
    setLoadingInvestisseurs(true);
    try {
      const response = await portfolioAPI.getInvestisseurs();
      if (response.data) {
        setInvestisseurs(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement investisseurs:', error);
      showToast('Erreur lors du chargement des investisseurs', 'error', 5000, null, 'Compte', pageIcon);
    } finally {
      setLoadingInvestisseurs(false);
      const duration = performance.now() - t0;
      logDataLoad('Compte - Investisseurs', duration);
    }
  }, [showToast, pageIcon]);
  
  const loadInvestisseursValues = useCallback(async () => {
    const t0 = performance.now();
    try {
      const response = await portfolioAPI.getInvestisseursValues();
      if (response.data) {
        setInvestisseursValues(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement valeurs investisseurs:', error);
    } finally {
      const duration = performance.now() - t0;
      logDataLoad('Compte - Valeurs investisseurs', duration, 'TRI + proportions');
    }
  }, []);
  
  // Tableau Valorisations Compte = Valorisation nette uniquement. API GET /portfolio/valeur-nette → portfolio_valeur_nette (alimenté à 22h30).
  const loadValorisations = useCallback(async () => {
    const t0 = performance.now();
    setLoadingValorisations(true);
    try {
      const response = await portfolioAPI.getValeurNette();
      if (response.data) {
        setValorisations(response.data);
      } else {
        setValorisations([]);
      }
    } catch (error) {
      console.error('Erreur chargement valorisations:', error);
      showToast('Erreur lors du chargement des valorisations', 'error', 5000, null, 'Compte', pageIcon);
      setValorisations([]);
    } finally {
      setLoadingValorisations(false);
      const duration = performance.now() - t0;
      logDataLoad('Compte - Valorisations', duration);
    }
  }, [showToast, pageIcon]);
  
  const loadCommentaires = useCallback(async () => {
    const t0 = performance.now();
    setLoadingCommentaires(true);
    try {
      const response = await portfolioAPI.getCommentaires();
      if (response.data) {
        setCommentaires(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement commentaires:', error);
      showToast('Erreur lors du chargement des commentaires', 'error', 5000, null, 'Compte', pageIcon);
    } finally {
      setLoadingCommentaires(false);
      const duration = performance.now() - t0;
      logDataLoad('Compte - Commentaires', duration);
    }
  }, [showToast, pageIcon]);
  
  // Fonctions de gestion CRUD
  const handleSaveApport = useCallback(async () => {
    try {
      const montantAbs = Math.abs(parseFloat(apportForm.montant) || 0);
      const montantSigne = apportForm.type_flux === 'retrait' ? montantAbs : -montantAbs;
      const data = {
        date_apport: apportForm.date_apport,
        montant: montantSigne,
        description: apportForm.description || null,
        investisseur: apportForm.investisseur || null
      };
      
      if (editingApport) {
        await portfolioAPI.updateApport(editingApport.id, data);
        showToast('Apport modifié avec succès', 'success', 3000, null, 'Compte', pageIcon);
      } else {
        await portfolioAPI.createApport(data);
        showToast('Apport créé avec succès', 'success', 3000, null, 'Compte', pageIcon);
      }
      
      setShowAddApportModal(false);
      setShowEditApportModal(false);
      setEditingApport(null);
      setApportForm({
        date_apport: new Date().toISOString().split('T')[0],
        type_flux: 'apport',
        montant: '',
        description: '',
        investisseur: ''
      });
      loadApports();
      loadTRIData(); // Recharger les données TRI
    } catch (error) {
      console.error('Erreur sauvegarde apport:', error);
      showToast('Erreur lors de la sauvegarde', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [apportForm, editingApport, showToast, pageIcon, loadApports, loadTRIData]);
  
  const handleDeleteApports = useCallback(async () => {
    try {
      await Promise.all(selectedApports.map(id => portfolioAPI.deleteApport(id)));
      showToast(`${selectedApports.length} apport(s) supprimé(s)`, 'success', 3000, null, 'Compte', pageIcon);
      setSelectedApports([]);
      loadApports();
      loadTRIData();
    } catch (error) {
      console.error('Erreur suppression apports:', error);
      showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [selectedApports, showToast, pageIcon, loadApports, loadTRIData]);
  
  const handleSaveInvestisseur = useCallback(async () => {
    try {
      const data = {
        nom: investisseurForm.nom,
        prenom: investisseurForm.prenom || null,
        type_investisseur: investisseurForm.type_investisseur,
        email: investisseurForm.email || null,
        telephone: investisseurForm.telephone || null,
        adresse: investisseurForm.adresse || null,
        commentaires: investisseurForm.commentaires || null
      };
      
      if (editingInvestisseur) {
        await portfolioAPI.updateInvestisseur(editingInvestisseur.id, data);
        showToast('Investisseur modifié avec succès', 'success', 3000, null, 'Compte', pageIcon);
      } else {
        await portfolioAPI.createInvestisseur(data);
        showToast('Investisseur créé avec succès', 'success', 3000, null, 'Compte', pageIcon);
      }
      
      setShowAddInvestisseurModal(false);
      setShowEditInvestisseurModal(false);
      setEditingInvestisseur(null);
      setInvestisseurForm({
        nom: '',
        prenom: '',
        type_investisseur: 'personne_physique',
        email: '',
        telephone: '',
        adresse: '',
        commentaires: ''
      });
      loadInvestisseurs();
      loadInvestisseursValues();
    } catch (error) {
      console.error('Erreur sauvegarde investisseur:', error);
      showToast('Erreur lors de la sauvegarde', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [investisseurForm, editingInvestisseur, showToast, pageIcon, loadInvestisseurs, loadInvestisseursValues]);
  
  const handleDeleteInvestisseurs = useCallback(async () => {
    try {
      await Promise.all(selectedInvestisseurs.map(id => portfolioAPI.deleteInvestisseur(id)));
      showToast(`${selectedInvestisseurs.length} investisseur(s) supprimé(s)`, 'success', 3000, null, 'Compte', pageIcon);
      setSelectedInvestisseurs([]);
      loadInvestisseurs();
      loadInvestisseursValues();
    } catch (error) {
      console.error('Erreur suppression investisseurs:', error);
      showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [selectedInvestisseurs, showToast, pageIcon, loadInvestisseurs, loadInvestisseursValues]);
  
  const handleSaveValorisation = useCallback(async () => {
    if (!valorisationForm.valeur_nette) return;
    try {
      const data = {
        date_valorisation: valorisationForm.date_valorisation,
        valeur_nette: parseFloat(valorisationForm.valeur_nette),
        currency: valorisationForm.currency || 'EUR',
        commentaire: null
      };
      if (editingValorisation) {
        await portfolioAPI.updateValeurNette(editingValorisation.id, data);
        showToast('Valeur nette modifiée avec succès', 'success', 3000, null, 'Compte', pageIcon);
      } else {
        await portfolioAPI.createValeurNette(data);
        showToast('Valeur nette créée avec succès', 'success', 3000, null, 'Compte', pageIcon);
      }
      setShowAddValorisationModal(false);
      setShowEditValorisationModal(false);
      setEditingValorisation(null);
      setValorisationForm({
        date_valorisation: new Date().toISOString().split('T')[0],
        valeur_nette: '',
        currency: 'EUR'
      });
      await loadValorisations();
      loadPerformanceData();
      loadTRIData();
    } catch (error) {
      console.error('Erreur sauvegarde valeur nette:', error);
      showToast('Erreur lors de la sauvegarde', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [valorisationForm, editingValorisation, showToast, pageIcon, loadValorisations, loadPerformanceData, loadTRIData]);
  
  const handleDeleteValorisations = useCallback(async () => {
    try {
      await Promise.all(selectedValorisations.map(id => portfolioAPI.deleteValeurNette(id)));
      showToast(`${selectedValorisations.length} valeur(s) nette(s) supprimée(s)`, 'success', 3000, null, 'Compte', pageIcon);
      setSelectedValorisations([]);
      await loadValorisations();
      loadPerformanceData();
      loadTRIData();
    } catch (error) {
      console.error('Erreur suppression valeurs nettes:', error);
      showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [selectedValorisations, showToast, pageIcon, loadValorisations, loadPerformanceData, loadTRIData]);

  const handleImportValorisationsCsv = useCallback(async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setLoadingImportCsv(true);
    try {
      const res = await portfolioAPI.importValeurNetteCsv(file);
      const data = res?.data || {};
      const inserted = data.inserted ?? 0;
      const skipped = data.skipped_dup ?? 0;
      const err = data.skipped_err ?? 0;
      showToast(
        `${inserted} ligne(s) importée(s), ${skipped} déjà en base, ${err} ignorée(s).`,
        inserted > 0 ? 'success' : 'info',
        5000,
        null,
        'Compte',
        pageIcon
      );
      await loadValorisations();
      loadPerformanceData();
      loadTRIData();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Erreur lors de l\'import';
      showToast(msg, 'error', 6000, null, 'Compte', pageIcon);
    } finally {
      setLoadingImportCsv(false);
      e.target.value = '';
    }
  }, [showToast, pageIcon, loadValorisations, loadPerformanceData, loadTRIData]);
  
  const handleSaveCommentaire = useCallback(async () => {
    try {
      const data = {
        date_commentaire: commentaireForm.date_commentaire,
        commentaire: commentaireForm.commentaire
      };
      
      if (editingCommentaire) {
        await portfolioAPI.updateCommentaire(editingCommentaire.id, data);
        showToast('Commentaire modifié avec succès', 'success', 3000, null, 'Compte', pageIcon);
      } else {
        await portfolioAPI.createCommentaire(data);
        showToast('Commentaire créé avec succès', 'success', 3000, null, 'Compte', pageIcon);
      }
      
      setShowAddCommentaireModal(false);
      setShowEditCommentaireModal(false);
      setEditingCommentaire(null);
      setCommentaireForm({
        date_commentaire: new Date().toISOString().split('T')[0],
        commentaire: ''
      });
      loadCommentaires();
    } catch (error) {
      console.error('Erreur sauvegarde commentaire:', error);
      showToast('Erreur lors de la sauvegarde', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [commentaireForm, editingCommentaire, showToast, pageIcon, loadCommentaires]);
  
  const handleDeleteCommentaires = useCallback(async () => {
    try {
      await Promise.all(selectedCommentaires.map(id => portfolioAPI.deleteCommentaire(id)));
      showToast(`${selectedCommentaires.length} commentaire(s) supprimé(s)`, 'success', 3000, null, 'Compte', pageIcon);
      setSelectedCommentaires([]);
      loadCommentaires();
    } catch (error) {
      console.error('Erreur suppression commentaires:', error);
      showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
    }
  }, [selectedCommentaires, showToast, pageIcon, loadCommentaires]);
  
  // Calculer les métriques TRI
  const triMetrics = useMemo(() => {
    if (!triData) return null;
    
    const netInvesti = triData.totalApports - triData.totalRetraits;
    const plusValue = triData.valorisation - netInvesti;
    const pvPct = netInvesti > 0 ? (plusValue / netInvesti * 100) : 0;
    
    return {
      ...triData,
      netInvesti,
      plusValue,
      pvPct
    };
  }, [triData]);
  
  // Préparer les données pour le graphique d'évolution (valeur + apports cumulés par date)
  // Cumul = tous les apports (et retraits hors "Tous le monde") jusqu'à chaque date → la courbe démarre au total déjà investi avant la période
  const chartData = useMemo(() => {
    if (!valuationEvolution?.data) return [];
    const contributions = (apports || []).filter(a => a.actif && parseFloat(a.montant) < 0);
    const isTousLeMonde = (inv) => !inv || String(inv).trim() === '' || /^tous?\s*le\s*monde$/i.test(String(inv).trim());
    const retraitsHorsTousLeMonde = (apports || []).filter(
      a => a.actif && parseFloat(a.montant) > 0 && !isTousLeMonde(a.investisseur)
    );
    return valuationEvolution.data.map(item => {
      const dateItem = new Date(item.date).getTime();
      const contribCumul = contributions.reduce((sum, a) => {
        const da = new Date(a.date_apport).getTime();
        return da <= dateItem ? sum + Math.abs(parseFloat(a.montant)) : sum;
      }, 0);
      const retraitsCumul = retraitsHorsTousLeMonde.reduce((sum, a) => {
        const da = new Date(a.date_apport).getTime();
        return da <= dateItem ? sum + parseFloat(a.montant) : sum;
      }, 0);
      const apportsCumul = contribCumul - retraitsCumul;
      return {
        date: item.date,
        valeur: item.valeur_totale,
        apportsCumul
      };
    });
  }, [valuationEvolution, apports]);

  // ATH pour la ligne de référence ; apports = courbe cumulée (plus de ref horizontale)
  const chartRefLines = useMemo(() => {
    const ath = chartData.length > 0 ? Math.max(...chartData.map(d => d.valeur)) : null;
    return { ath };
  }, [chartData]);
  
  // Colonnes pour les tableaux
  const apportsColumns = [
    { key: 'date_apport', label: 'Date', align: 'left' },
    { key: 'montant', label: 'Montant', align: 'right' },
    { key: 'description', label: 'Description', align: 'left' },
    { key: 'investisseur', label: 'Investisseur', align: 'left' }
  ];
  
  const investisseursColumns = [
    { key: 'nom', label: 'Nom', align: 'left' },
    { key: 'prenom', label: 'Prénom', align: 'left' },
    { key: 'type_investisseur', label: 'Type', align: 'left' },
    { key: 'email', label: 'Email', align: 'left' }
  ];
  
  const valorisationsColumns = [
    { key: 'date_valorisation', label: 'Date', align: 'left' },
    { key: 'valeur_nette', label: 'Valeur Nette', align: 'right' }
  ];
  
  const commentairesColumns = [
    { key: 'date_commentaire', label: 'Date', align: 'left' },
    { key: 'commentaire', label: 'Commentaire', align: 'left' }
  ];
  
  // Formatage des données pour les tableaux
  const formattedApports = useMemo(() => {
    return apports.map(a => ({
      ...a,
      id: a.id,
      date_apport: new Date(a.date_apport).toLocaleDateString('fr-FR'),
      montant: formatCurrency(parseFloat(a.montant), currency, 2),
      investisseur: a.investisseur || 'Tout le monde'
    }));
  }, [apports, currency]);
  
  const formattedInvestisseurs = useMemo(() => {
    return investisseurs.map(i => ({
      ...i,
      id: i.id,
      nom_complet: i.prenom ? `${i.prenom} ${i.nom}` : i.nom
    }));
  }, [investisseurs]);
  
  const formattedValorisations = useMemo(() => {
    return valorisations.map(v => ({
      ...v,
      id: v.id,
      date_valorisation: new Date(v.date_valorisation).toLocaleDateString('fr-FR'),
      valeur_nette: formatCurrency(parseFloat(v.valeur_nette), v.currency || currency, 2)
    }));
  }, [valorisations, currency]);
  
  const formattedCommentaires = useMemo(() => {
    return commentaires.map(c => ({
      ...c,
      id: c.id,
      date_commentaire: new Date(c.date_commentaire).toLocaleDateString('fr-FR')
    }));
  }, [commentaires]);
  
  // Filtrer les onglets selon allowed_tabs (null/vide = tout autorisé)
  const canAccessCompteTab = useCallback((tabId) => {
    if (!profile) return true;
    if (profile.is_superadmin || profile.is_owner) return true;
    const raw = profile.allowed_tabs;
    const allowed = Array.isArray(raw) ? raw : [];
    if (allowed.length === 0) return true;
    return allowed.includes(tabId);
  }, [profile?.allowed_tabs, profile?.is_superadmin, profile?.is_owner]);

  // Définir les onglets (avec tabId pour le filtrage)
  const allTabs = [
    {
      tabId: 'compte-performance',
      label: 'Performance',
      content: (
        <div className="compte-tab-content">
          {loading ? (
            <Spinner />
          ) : (
            <>
              {/* Évolution de la valeur du compte */}
              <section className="compte-section compte-section-evolution" data-name="evolution_compte">
                <Title level={2}>Évolution de la valeur du compte</Title>
                <div style={{ marginTop: '1.5rem' }}>
                {valuationEvolution?.data && chartData.length > 0 ? (
                  <>
                    {/* Métriques de risque - Carrousel */}
                    {valuationEvolution.metrics && (
                      <div className="metrics-carousel-container" style={{ marginBottom: '1rem' }}>
                        <button className="carousel-btn left" onClick={() => scrollCarousel(carouselRefRisk, 'left')} aria-label="Défiler vers la gauche">←</button>
                        <div className="metrics-carousel" ref={carouselRefRisk}>
                          {valuationEvolution.metrics.beta !== null && (
                            <div className="metric-card">
                              <Metric label="Beta" value={valuationEvolution.metrics.beta?.toFixed(1) || 'N/A'} help="Mesure la sensibilité du portefeuille aux mouvements du marché" />
                            </div>
                          )}
                          {valuationEvolution.metrics.alpha !== null && (
                            <div className="metric-card">
                              <Metric label="Alpha" value={valuationEvolution.metrics.alpha ? `${valuationEvolution.metrics.alpha.toFixed(1)}%` : 'N/A'} help="Rendement excédentaire par rapport au marché" />
                            </div>
                          )}
                          {valuationEvolution.metrics.max_drawdown !== null && (
                            <div className="metric-card">
                              <Metric label="Max Drawdown" value={valuationEvolution.metrics.max_drawdown ? `${valuationEvolution.metrics.max_drawdown.toFixed(1)}%` : 'N/A'} help="Perte maximale depuis un pic" />
                            </div>
                          )}
                          {valuationEvolution.metrics.sortino_ratio !== null && (
                            <div className="metric-card">
                              <Metric label="Sortino" value={valuationEvolution.metrics.sortino_ratio?.toFixed(1) || 'N/A'} help="Ratio de Sharpe ajusté pour la volatilité baissière" />
                            </div>
                          )}
                          {valuationEvolution.metrics.tracking_error !== null && (
                            <div className="metric-card">
                              <Metric label="Tracking Error" value={valuationEvolution.metrics.tracking_error ? `${valuationEvolution.metrics.tracking_error.toFixed(1)}%` : 'N/A'} help="Écart-type des rendements excédentaires" />
                            </div>
                          )}
                          {valuationEvolution.metrics.information_ratio !== null && (
                            <div className="metric-card">
                              <Metric label="Info Ratio" value={valuationEvolution.metrics.information_ratio?.toFixed(1) || 'N/A'} help="Rendement excédentaire ajusté du risque de tracking" />
                            </div>
                          )}
                        </div>
                        <button className="carousel-btn right" onClick={() => scrollCarousel(carouselRefRisk, 'right')} aria-label="Défiler vers la droite">→</button>
                      </div>
                    )}
                    
                    <div ref={chartContainerRef} className="compte-chart-container" style={{ height: '400px', minHeight: '400px', marginBottom: '1rem' }}>
                      {isChartVisible && chartContainerDims.width > 0 && chartContainerDims.height > 0 ? (
                        <ResponsiveContainer key={`chart-${chartKey}`} width="100%" height="100%" minHeight={400}>
                          <AreaChart data={chartData} margin={{ top: 10, right: 10, bottom: 5, left: 48 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tickFormatter={(value) => new Date(value).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })}
                              tick={{ style: { fontFamily: '"Segoe UI", "Source Sans", sans-serif', fontSize: 12 } }}
                            />
                            <YAxis 
                              tickFormatter={(value) => (value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' k' + getCurrencySymbol(currency)}
                              tick={{ style: { fontFamily: '"Segoe UI", "Source Sans", sans-serif', fontSize: 12 } }}
                            />
                            {chartRefLines.ath != null && (
                              <ReferenceLine y={chartRefLines.ath} stroke="var(--accent-color)" strokeDasharray="5 5" strokeWidth={1.5} />
                            )}
                            <Tooltip 
                              content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                const valValeur = payload.find(p => p.dataKey === 'valeur')?.value ?? payload[0]?.value;
                                const valApportsCumul = payload.find(p => p.dataKey === 'apportsCumul')?.value;
                                const row = (labelText, value, color = 'inherit') => (
                                  <div key={labelText} style={{ display: 'flex', justifyContent: 'space-between', gap: '24px', color }}>
                                    <span style={{ textAlign: 'left' }}>{labelText}</span>
                                    <span style={{ textAlign: 'right', fontWeight: 500, whiteSpace: 'nowrap' }}>{value}</span>
                                  </div>
                                );
                                return (
                                  <div className="compte-chart-tooltip">
                                    <div style={{ marginBottom: '8px', fontWeight: 600 }}>{label ? new Date(label).toLocaleDateString('fr-FR') : ''}</div>
                                    {row('Valeur', formatCurrency(valValeur ?? 0, currency, 0))}
                                    {chartRefLines.ath != null && row('ATH', formatCurrency(chartRefLines.ath, currency, 0))}
                                    {valApportsCumul != null && row('Apports cumulés', formatCurrency(valApportsCumul, currency, 0))}
                                  </div>
                                );
                              }}
                              wrapperStyle={{ fontFamily: '"Segoe UI", "Source Sans", sans-serif' }}
                            />
                            <Area 
                              type="monotone" 
                              dataKey="valeur" 
                              stroke="#1f1f1f" 
                              fill="#1f1f1f" 
                              fillOpacity={0.1}
                              name="Valeur"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="apportsCumul" 
                              stroke="#6b7280" 
                              strokeDasharray="5 5" 
                              strokeWidth={1.5} 
                              dot={false}
                              name="Apports cumulés"
                              connectNulls
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                          <span>Chargement du graphique...</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Métriques de performance - Carrousel */}
                    {valuationEvolution.metrics && (
                      <div className="metrics-carousel-container">
                        <button className="carousel-btn left" onClick={() => scrollCarousel(carouselRefPerf, 'left')} aria-label="Défiler vers la gauche">←</button>
                        <div className="metrics-carousel" ref={carouselRefPerf}>
                          <div className="metric-card">
                            <Metric label="TRI Total" value={valuationEvolution.metrics.total_return ? `${valuationEvolution.metrics.total_return.toFixed(1)}%` : 'N/A'} />
                          </div>
                          <div className="metric-card">
                            <Metric label="S&P 500 - TRI Total" value={valuationEvolution.metrics.sp500_total_return ? `${valuationEvolution.metrics.sp500_total_return.toFixed(1)}%` : 'N/A'} />
                          </div>
                          <div className="metric-card">
                            <Metric label="Rendement Annualisé" value={valuationEvolution.metrics.annualized_return ? `${valuationEvolution.metrics.annualized_return.toFixed(1)}%` : 'N/A'} />
                          </div>
                          <div className="metric-card">
                            <Metric label="Volatilité" value={valuationEvolution.metrics.volatility ? `${valuationEvolution.metrics.volatility.toFixed(1)}%` : 'N/A'} />
                          </div>
                          <div className="metric-card">
                            <Metric label="S&P 500 - Volatilité" value={valuationEvolution.metrics.sp500_vol ? `${valuationEvolution.metrics.sp500_vol.toFixed(1)}%` : 'N/A'} />
                          </div>
                          <div className="metric-card">
                            <Metric label="Ratio de Sharpe" value={valuationEvolution.metrics.sharpe_ratio?.toFixed(1) || 'N/A'} help="Mesure le rendement ajusté du risque" />
                          </div>
                        </div>
                        <button className="carousel-btn right" onClick={() => scrollCarousel(carouselRefPerf, 'right')} aria-label="Défiler vers la droite">→</button>
                      </div>
                    )}
                  </>
                ) : (
                  <p>Aucune donnée de valorisation disponible</p>
                )}
                </div>
              </section>
              
              {/* Evolution par investisseur */}
              <section className="compte-section">
                <Title level={2}>Evolution par investisseur</Title>
                <div style={{ marginTop: '1.5rem' }}>
                {investisseursValues.length > 0 ? (
                  <div className="metrics-carousel-container">
                    <button className="carousel-btn left" onClick={() => scrollCarousel(carouselRefInvestisseurs, 'left')} aria-label="Défiler vers la gauche">←</button>
                    <div className="metrics-carousel" ref={carouselRefInvestisseurs}>
                      {investisseursValues.map((inv, idx) => (
                        <div className="metric-card" key={idx}>
                          <Metric
                            label={inv.nom}
                            value={formatCurrency(inv.valeur, currency, 0)}
                            delta={inv.evolution_pct != null && !Number.isNaN(inv.evolution_pct) ? inv.evolution_pct : (inv.tri != null && !Number.isNaN(inv.tri) ? inv.tri : null)}
                            deltaDecimals={1}
                            currency=""
                            help={`Montant investi: ${formatCurrency(inv.apports, currency, 0)} | Détention: ${(inv.proportion * 100).toFixed(2)}%`}
                          />
                        </div>
                      ))}
                    </div>
                    <button className="carousel-btn right" onClick={() => scrollCarousel(carouselRefInvestisseurs, 'right')} aria-label="Défiler vers la droite">→</button>
                  </div>
                ) : (
                  <p>Aucun investisseur avec données disponibles</p>
                )}
                </div>
              </section>
            </>
          )}
        </div>
      )
    },
    {
      tabId: 'compte-synthese-tri',
      label: 'TRI',
      content: (
        <div className="compte-tab-content">
          <Title level={2}>Synthèse du TRI</Title>
          {loading ? (
            <Spinner />
          ) : triMetrics ? (
            <>
              {/* Métriques TRI - Carrousel */}
              <div className="metrics-carousel-container" style={{ marginBottom: '2rem', marginTop: '1.5rem' }}>
                <button className="carousel-btn left" onClick={() => scrollCarousel(carouselRefTri, 'left')} aria-label="Défiler vers la gauche">←</button>
                <div className="metrics-carousel" ref={carouselRefTri}>
                  <div className="metric-card">
                    <Metric label="TRI" value={triMetrics.tri !== null ? `${triMetrics.tri.toFixed(2)}%` : 'N/A'} />
                  </div>
                  <div className="metric-card">
                    <Metric label="Apports" value={formatCurrency(triMetrics.totalApports, currency, 0)} />
                  </div>
                  <div className="metric-card">
                    <Metric label="Retraits" value={formatCurrency(triMetrics.totalRetraits, currency, 0)} />
                  </div>
                  <div className="metric-card">
                    <Metric label="Net investi" value={formatCurrency(triMetrics.netInvesti, currency, 0)} />
                  </div>
                  <div className="metric-card">
                    <Metric label="Valorisation" value={formatCurrency(triMetrics.valorisation, currency, 0)} />
                  </div>
                  <div className="metric-card">
                    <Metric label="Plus-value" value={formatCurrency(triMetrics.plusValue, currency, 0)} />
                  </div>
                </div>
                <button className="carousel-btn right" onClick={() => scrollCarousel(carouselRefTri, 'right')} aria-label="Défiler vers la droite">→</button>
              </div>
            </>
          ) : (
            <p style={{ marginTop: '1.5rem' }}>Aucune donnée TRI disponible. Ajoutez des apports et une valorisation.</p>
          )}
        </div>
      )
    },
    {
      tabId: 'compte-pl',
      label: 'P&L',
      content: (
        <div className="compte-tab-content">
          <Title level={2}>P&L</Title>
          <div style={{ marginTop: '1.5rem' }}>
          {loading ? (
            <Spinner />
          ) : plData ? (
            <>
              {/* Métriques Daily/Weekly/Monthly High/Low - Carrousel */}
              {maxGainsLosses && (
                <div className="metrics-carousel-container" style={{ marginBottom: '1rem' }}>
                  <button className="carousel-btn left" onClick={() => scrollCarousel(carouselRefGainsLosses, 'left')} aria-label="Défiler vers la gauche">←</button>
                  <div className="metrics-carousel" ref={carouselRefGainsLosses}>
                    <div className="metric-card">
                      <Metric label="Daily Low" value={formatEuropeanCurrency(maxGainsLosses.perte_max_journalier, currency, 0)} help="Plus grande perte sur une journée" />
                    </div>
                    <div className="metric-card">
                      <Metric label="Daily High" value={formatEuropeanCurrency(maxGainsLosses.gain_max_journalier, currency, 0)} help="Plus grand gain sur une journée" />
                    </div>
                    <div className="metric-card">
                      <Metric label="Weekly Low" value={formatEuropeanCurrency(maxGainsLosses.perte_max_hebdo, currency, 0)} help="Plus grande perte sur 7 jours" />
                    </div>
                    <div className="metric-card">
                      <Metric label="Weekly High" value={formatEuropeanCurrency(maxGainsLosses.gain_max_hebdo, currency, 0)} help="Plus grand gain sur 7 jours" />
                    </div>
                    <div className="metric-card">
                      <Metric label="Monthly Low" value={formatEuropeanCurrency(maxGainsLosses.perte_max_mensuel, currency, 0)} help="Plus grande perte sur 30 jours" />
                    </div>
                    <div className="metric-card">
                      <Metric label="Monthly High" value={formatEuropeanCurrency(maxGainsLosses.gain_max_mensuel, currency, 0)} help="Plus grand gain sur 30 jours" />
                    </div>
                  </div>
                  <button className="carousel-btn right" onClick={() => scrollCarousel(carouselRefGainsLosses, 'right')} aria-label="Défiler vers la droite">→</button>
                </div>
              )}
              
              {/* Tableau P&L */}
              <Table
                name="pl"
                columns={[
                  { key: 'ligne', label: `en ${getCurrencySymbol(currency)}`, align: 'left' },
                  { key: 'D', label: 'D', align: 'right' },
                  { key: 'MTD', label: 'MTD', align: 'right' },
                  { key: 'YTD', label: 'YTD', align: 'right' },
                  { key: 'Period', label: 'Period', align: 'right' }
                ]}
                data={[
                  {
                    ligne: 'Rendement',
                    D: formatEuropeanCurrency(plData.day?.rendement || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.rendement || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.rendement || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.rendement || 0, currency, 0)
                  },
                  {
                    ligne: 'Dividendes',
                    D: formatEuropeanCurrency(plData.day?.dividendes_total || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.dividendes_total || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.dividendes_total || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.dividendes_total || 0, currency, 0)
                  },
                  {
                    ligne: '**Total produits**',
                    D: formatEuropeanCurrency(plData.day?.total_produits || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.total_produits || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.total_produits || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.total_produits || 0, currency, 0)
                  },
                  {
                    ligne: 'Commissions',
                    D: formatEuropeanCurrency(plData.day?.commissions || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.commissions || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.commissions || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.commissions || 0, currency, 0)
                  },
                  {
                    ligne: 'Frais de transaction',
                    D: formatEuropeanCurrency(plData.day?.frais_transaction || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.frais_transaction || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.frais_transaction || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.frais_transaction || 0, currency, 0)
                  },
                  {
                    ligne: 'Intérêts',
                    D: formatEuropeanCurrency(plData.day?.interets_total || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.interets_total || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.interets_total || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.interets_total || 0, currency, 0)
                  },
                  {
                    ligne: 'Changement de devises',
                    D: formatEuropeanCurrency(plData.day?.changement_change || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.changement_change || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.changement_change || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.changement_change || 0, currency, 0)
                  },
                  {
                    ligne: 'Taxes',
                    D: formatEuropeanCurrency(plData.day?.taxes || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.taxes || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.taxes || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.taxes || 0, currency, 0)
                  },
                  {
                    ligne: '**Total charges**',
                    D: formatEuropeanCurrency(plData.day?.total_charges || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.total_charges || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.total_charges || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.total_charges || 0, currency, 0)
                  },
                  {
                    ligne: '**Résultat**',
                    D: formatEuropeanCurrency(plData.day?.resultat || 0, currency, 0),
                    MTD: formatEuropeanCurrency(plData.month?.resultat || 0, currency, 0),
                    YTD: formatEuropeanCurrency(plData.ytd?.resultat || 0, currency, 0),
                    Period: formatEuropeanCurrency(plData.period?.resultat || 0, currency, 0)
                  },
                  {
                    ligne: '*Résultat (%)*',
                    D: formatEuropeanCurrency(plData.day?.resultat_produits || 0, currency, 0, true),
                    MTD: formatEuropeanCurrency(plData.month?.resultat_produits || 0, currency, 0, true),
                    YTD: formatEuropeanCurrency(plData.ytd?.resultat_produits || 0, currency, 0, true),
                    Period: formatEuropeanCurrency(plData.period?.resultat_produits || 0, currency, 0, true)
                  }
                ]}
              />
            </>
          ) : (
            <p>Aucune donnée P&L disponible</p>
          )}
          </div>
        </div>
      )
    },
    {
      tabId: 'compte-apports-retraits',
      label: 'Apports/Retraits',
      content: (
        <div className="compte-tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <Title level={2}>Apports/Retraits</Title>
            {canEdit && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button variant="secondary" onClick={() => {
                setEditingApport(null);
                setApportForm({
                  date_apport: new Date().toISOString().split('T')[0],
                  type_flux: 'apport',
                  montant: '',
                  description: '',
                  investisseur: ''
                });
                setShowAddApportModal(true);
              }} style={{ minWidth: '100px' }}>Ajouter</Button>
            </div>
            )}
          </div>
          
          {loadingApports ? (
            <Spinner />
          ) : (
            <Table
              name="apports_retraits"
              columns={apportsColumns}
              data={formattedApports}
              onRowClick={canEdit ? (row) => {
                const apport = apports.find(a => a.id === row.id);
                if (apport) {
                  setEditingApport(apport);
                  const montantNum = parseFloat(apport.montant);
                  setApportForm({
                    date_apport: apport.date_apport,
                    type_flux: montantNum < 0 ? 'apport' : 'retrait',
                    montant: Math.abs(montantNum).toString(),
                    description: apport.description || '',
                    investisseur: apport.investisseur || ''
                  });
                  setShowEditApportModal(true);
                }
              } : undefined}
            />
          )}
          
          {/* Modal Ajouter/Modifier Apport */}
          <Modal
            isOpen={showAddApportModal || showEditApportModal}
            onClose={() => {
              setShowAddApportModal(false);
              setShowEditApportModal(false);
              setEditingApport(null);
              setApportForm({
                date_apport: new Date().toISOString().split('T')[0],
                type_flux: 'apport',
                montant: '',
                description: '',
                investisseur: ''
              });
            }}
            title={editingApport ? 'Modifier un flux' : 'Ajouter un flux'}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <DateInput
                label="Date"
                value={apportForm.date_apport ? new Date(apportForm.date_apport) : null}
                onChange={(date) => setApportForm({ ...apportForm, date_apport: date ? date.toISOString().split('T')[0] : '' })}
              />
              <Selectbox
                label="Type de flux"
                options={[
                  { value: 'apport', label: 'Apport' },
                  { value: 'retrait', label: 'Retrait' }
                ]}
                value={apportForm.type_flux}
                onChange={(value) => setApportForm({ ...apportForm, type_flux: value })}
              />
              <NumberInput
                label="Montant"
                value={apportForm.montant}
                onChange={(val) => setApportForm(prev => ({
                  ...prev,
                  montant: val !== null && val !== undefined ? String(val) : ''
                }))}
                help="Montant en valeur absolue"
              />
              <input
                type="text"
                placeholder="Description"
                value={apportForm.description}
                onChange={(e) => setApportForm({ ...apportForm, description: e.target.value })}
                className="compte-modal-input"
              />
              <Selectbox
                label="Investisseur"
                options={[
                  { value: '', label: 'Tout le monde' },
                  ...investisseurs.map(i => ({
                    value: i.prenom ? `${i.prenom} ${i.nom}` : i.nom,
                    label: i.prenom ? `${i.prenom} ${i.nom}` : i.nom
                  }))
                ]}
                value={apportForm.investisseur}
                onChange={(value) => setApportForm({ ...apportForm, investisseur: value })}
              />
              <div className="form-actions compte-modal-actions">
                {editingApport && (
                  <Button 
                    variant="secondary"
                    onClick={async () => {
                      if (window.confirm('Êtes-vous sûr de vouloir supprimer cet apport/retrait ?')) {
                        try {
                          await portfolioAPI.deleteApport(editingApport.id);
                          showToast('Apport supprimé avec succès', 'success', 3000, null, 'Compte', pageIcon);
                          setShowEditApportModal(false);
                          setEditingApport(null);
                          loadApports();
                          loadTRIData();
                        } catch (error) {
                          console.error('Erreur suppression apport:', error);
                          showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
                        }
                      }
                    }}
                    style={{ backgroundColor: '#dc3545', color: 'white' }}
                  >
                    Supprimer
                  </Button>
                )}
                <Button
                  onClick={handleSaveApport}
                  disabled={!apportForm.date_apport || apportForm.montant === ''}
                >
                  {editingApport ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      )
    },
    {
      tabId: 'compte-investisseurs',
      label: 'Investisseurs',
      content: (
        <div className="compte-tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <Title level={2}>Investisseurs</Title>
            {canEdit && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button variant="secondary" onClick={() => {
                setEditingInvestisseur(null);
                setInvestisseurForm({
                  nom: '',
                  prenom: '',
                  type_investisseur: 'personne_physique',
                  email: '',
                  telephone: '',
                  adresse: '',
                  commentaires: ''
                });
                setShowAddInvestisseurModal(true);
              }} style={{ minWidth: '100px' }}>Ajouter</Button>
            </div>
            )}
          </div>
          
          {loadingInvestisseurs ? (
            <Spinner />
          ) : (
            <Table
              name="investisseurs"
              columns={investisseursColumns}
              data={formattedInvestisseurs}
              onRowClick={canEdit ? (row) => {
                const inv = investisseurs.find(i => i.id === row.id);
                if (inv) {
                  setEditingInvestisseur(inv);
                  setInvestisseurForm({
                    nom: inv.nom,
                    prenom: inv.prenom || '',
                    type_investisseur: inv.type_investisseur || 'personne_physique',
                    email: inv.email || '',
                    telephone: inv.telephone || '',
                    adresse: inv.adresse || '',
                    commentaires: inv.commentaires || ''
                  });
                  setShowEditInvestisseurModal(true);
                }
              } : undefined}
            />
          )}
          
          {/* Modal Ajouter/Modifier Investisseur */}
          <Modal
            isOpen={showAddInvestisseurModal || showEditInvestisseurModal}
            onClose={() => {
              setShowAddInvestisseurModal(false);
              setShowEditInvestisseurModal(false);
              setEditingInvestisseur(null);
              setInvestisseurForm({
                nom: '',
                prenom: '',
                type_investisseur: 'personne_physique',
                email: '',
                telephone: '',
                adresse: '',
                commentaires: ''
              });
            }}
            title={editingInvestisseur ? 'Modifier un investisseur' : 'Ajouter un investisseur'}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <input
                type="text"
                placeholder="Nom *"
                value={investisseurForm.nom}
                onChange={(e) => setInvestisseurForm({ ...investisseurForm, nom: e.target.value })}
                required
                className="compte-modal-input"
              />
              <input
                type="text"
                placeholder="Prénom"
                value={investisseurForm.prenom}
                onChange={(e) => setInvestisseurForm({ ...investisseurForm, prenom: e.target.value })}
                className="compte-modal-input"
              />
              <Selectbox
                label="Type"
                options={[
                  { value: 'personne_physique', label: 'Personne physique' },
                  { value: 'personne_morale', label: 'Personne morale' }
                ]}
                value={investisseurForm.type_investisseur}
                onChange={(value) => setInvestisseurForm({ ...investisseurForm, type_investisseur: value })}
              />
              <input
                type="email"
                placeholder="Email"
                value={investisseurForm.email}
                onChange={(e) => setInvestisseurForm({ ...investisseurForm, email: e.target.value })}
                className="compte-modal-input"
              />
              <input
                type="tel"
                placeholder="Téléphone"
                value={investisseurForm.telephone}
                onChange={(e) => setInvestisseurForm({ ...investisseurForm, telephone: e.target.value })}
                className="compte-modal-input"
              />
              <textarea
                placeholder="Adresse"
                value={investisseurForm.adresse}
                onChange={(e) => setInvestisseurForm({ ...investisseurForm, adresse: e.target.value })}
                rows={3}
                className="compte-modal-input"
              />
              <textarea
                placeholder="Commentaires"
                value={investisseurForm.commentaires}
                onChange={(e) => setInvestisseurForm({ ...investisseurForm, commentaires: e.target.value })}
                rows={3}
                className="compte-modal-input"
              />
              <div className="form-actions compte-modal-actions">
                {editingInvestisseur && (
                  <Button 
                    variant="secondary"
                    onClick={async () => {
                      if (window.confirm('Êtes-vous sûr de vouloir supprimer cet investisseur ?')) {
                        try {
                          await portfolioAPI.deleteInvestisseur(editingInvestisseur.id);
                          showToast('Investisseur supprimé avec succès', 'success', 3000, null, 'Compte', pageIcon);
                          setShowEditInvestisseurModal(false);
                          setEditingInvestisseur(null);
                          loadInvestisseurs();
                          loadInvestisseursValues();
                        } catch (error) {
                          console.error('Erreur suppression investisseur:', error);
                          showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
                        }
                      }
                    }}
                    style={{ backgroundColor: '#dc3545', color: 'white' }}
                  >
                    Supprimer
                  </Button>
                )}
                <Button onClick={handleSaveInvestisseur} disabled={!investisseurForm.nom}>
                  {editingInvestisseur ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      )
    },
    {
      tabId: 'compte-valorisations',
      label: 'Valorisations',
      content: (
        <div className="compte-tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <Title level={2}>Valorisations</Title>
            {canEdit && (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Import CSV masqué (code conservé pour réactivation) */}
              {false && (
              <>
              <input
                ref={valorisationCsvInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleImportValorisationsCsv}
              />
              <Button
                variant="secondary"
                onClick={() => valorisationCsvInputRef.current?.click()}
                disabled={loadingImportCsv}
                style={{ minWidth: '100px' }}
              >
                {loadingImportCsv ? 'Import…' : 'Importer CSV'}
              </Button>
              </>
              )}
              <Button variant="secondary" onClick={() => {
                setEditingValorisation(null);
                setValorisationForm({
                  date_valorisation: new Date().toISOString().split('T')[0],
                  valeur_nette: '',
                  currency: 'EUR'
                });
                setShowAddValorisationModal(true);
              }} style={{ minWidth: '100px' }}>Ajouter</Button>
            </div>
            )}
          </div>
          
          {loadingValorisations ? (
            <Spinner />
          ) : (
            <Table
              name="valeur_nette"
              columns={valorisationsColumns}
              data={formattedValorisations}
              onRowClick={canEdit ? (row) => {
                const val = valorisations.find(v => v.id === row.id);
                if (val) {
                  setEditingValorisation(val);
                  setValorisationForm({
                    date_valorisation: val.date_valorisation,
                    valeur_nette: (val.valeur_nette != null ? val.valeur_nette : '').toString(),
                    currency: val.currency || 'EUR'
                  });
                  setShowEditValorisationModal(true);
                }
              } : undefined}
            />
          )}
          
          {/* Modal Ajouter/Modifier Valorisation */}
          <Modal
            isOpen={showAddValorisationModal || showEditValorisationModal}
            onClose={() => {
              setShowAddValorisationModal(false);
              setShowEditValorisationModal(false);
              setEditingValorisation(null);
              setValorisationForm({
                date_valorisation: new Date().toISOString().split('T')[0],
                valeur_nette: '',
                currency: 'EUR'
              });
            }}
            title={editingValorisation ? 'Modifier une valeur nette' : 'Ajouter une valeur nette'}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <DateInput
                label="Date"
                value={valorisationForm.date_valorisation ? new Date(valorisationForm.date_valorisation) : null}
                onChange={(date) => setValorisationForm({ ...valorisationForm, date_valorisation: date ? date.toISOString().split('T')[0] : '' })}
              />
              <NumberInput
                label="Valeur Nette"
                value={valorisationForm.valeur_nette}
                onChange={(val) => setValorisationForm({ ...valorisationForm, valeur_nette: val === null || val === undefined ? '' : String(val) })}
              />
              <div className="form-actions compte-modal-actions">
                {editingValorisation && (
                  <Button 
                    variant="secondary"
                    onClick={async () => {
                      if (window.confirm('Êtes-vous sûr de vouloir supprimer cette valeur nette ?')) {
                        try {
                          await portfolioAPI.deleteValeurNette(editingValorisation.id);
                          showToast('Valeur nette supprimée avec succès', 'success', 3000, null, 'Compte', pageIcon);
                          setShowEditValorisationModal(false);
                          setEditingValorisation(null);
                          await loadValorisations();
                          loadPerformanceData();
                          loadTRIData();
                        } catch (error) {
                          console.error('Erreur suppression valeur nette:', error);
                          showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
                        }
                      }
                    }}
                    style={{ backgroundColor: '#dc3545', color: 'white' }}
                  >
                    Supprimer
                  </Button>
                )}
                <Button variant="primary" onClick={handleSaveValorisation} disabled={!valorisationForm.valeur_nette}>
                  {editingValorisation ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      )
    },
    {
      tabId: 'compte-commentaires',
      label: 'Commentaires',
      content: (
        <div className="compte-tab-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.5rem' }}>
            <Title level={2}>Commentaires</Title>
            {canEdit && (
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button variant="secondary" onClick={() => {
                setEditingCommentaire(null);
                setCommentaireForm({
                  date_commentaire: new Date().toISOString().split('T')[0],
                  commentaire: ''
                });
                setShowAddCommentaireModal(true);
              }} style={{ minWidth: '100px' }}>Ajouter</Button>
            </div>
            )}
          </div>
          
          {loadingCommentaires ? (
            <Spinner />
          ) : (
            <Table
              name="commentaires"
              columns={commentairesColumns}
              data={formattedCommentaires}
              onRowClick={canEdit ? (row) => {
                const com = commentaires.find(c => c.id === row.id);
                if (com) {
                  setEditingCommentaire(com);
                  setCommentaireForm({
                    date_commentaire: com.date_commentaire,
                    commentaire: com.commentaire
                  });
                  setShowEditCommentaireModal(true);
                }
              } : undefined}
            />
          )}
          
          {/* Modal Ajouter/Modifier Commentaire */}
          <Modal
            isOpen={showAddCommentaireModal || showEditCommentaireModal}
            onClose={() => {
              setShowAddCommentaireModal(false);
              setShowEditCommentaireModal(false);
              setEditingCommentaire(null);
              setCommentaireForm({
                date_commentaire: new Date().toISOString().split('T')[0],
                commentaire: ''
              });
            }}
            title={editingCommentaire ? 'Modifier un commentaire' : 'Ajouter un commentaire'}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <DateInput
                label="Date"
                value={commentaireForm.date_commentaire ? new Date(commentaireForm.date_commentaire) : null}
                onChange={(date) => setCommentaireForm({ ...commentaireForm, date_commentaire: date ? date.toISOString().split('T')[0] : '' })}
              />
              <textarea
                placeholder="Commentaire *"
                value={commentaireForm.commentaire}
                onChange={(e) => setCommentaireForm({ ...commentaireForm, commentaire: e.target.value })}
                rows={5}
                required
                className="compte-modal-input"
              />
              <div className="form-actions compte-modal-actions">
                {editingCommentaire && (
                  <Button 
                    variant="secondary"
                    onClick={async () => {
                      if (window.confirm('Êtes-vous sûr de vouloir supprimer ce commentaire ?')) {
                        try {
                          await portfolioAPI.deleteCommentaire(editingCommentaire.id);
                          showToast('Commentaire supprimé avec succès', 'success', 3000, null, 'Compte', pageIcon);
                          setShowEditCommentaireModal(false);
                          setEditingCommentaire(null);
                          loadCommentaires();
                        } catch (error) {
                          console.error('Erreur suppression commentaire:', error);
                          showToast('Erreur lors de la suppression', 'error', 5000, null, 'Compte', pageIcon);
                        }
                      }
                    }}
                    style={{ backgroundColor: '#dc3545', color: 'white' }}
                  >
                    Supprimer
                  </Button>
                )}
                <Button onClick={handleSaveCommentaire} disabled={!commentaireForm.commentaire}>
                  {editingCommentaire ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      )
    }
  ];

  // Filtrer selon les droits et extraire label/content pour le composant Tabs
  const filteredTabs = useMemo(() => {
    return allTabs
      .filter(t => canAccessCompteTab(t.tabId))
      .map(({ label, content }) => ({ label, content }));
  }, [allTabs, canAccessCompteTab]);

  // Réajuster activeTab si l'onglet actuel n'est plus accessible
  useEffect(() => {
    if (activeTab >= filteredTabs.length && filteredTabs.length > 0) {
      setActiveTab(0);
    }
  }, [activeTab, filteredTabs.length]);

  return (
    <div className="compte-page">
      <Tabs 
        tabs={filteredTabs}
        defaultTab={activeTab}
        onChange={setActiveTab}
      />
    </div>
  );
};

export default Compte;
